/**
 * Hook that returns the set of entity type names that are stage types.
 *
 * Uses the shared 'object-types-all' React Query cache so no additional
 * API calls are made when other hooks (useEntityTypeSchema, useSchemaLabels)
 * have already fetched the data.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from './useEntityTypeSchema';

export interface StageTypeNamesResult {
  /** Set of stage entity type names (empty while loading) */
  stageTypeNames: Set<string>;
  /** True while the object types are being fetched */
  isLoading: boolean;
}

/**
 * Returns a stable Set of entity type names that have "stage" in their entity_kind,
 * along with a loading flag.
 *
 * Used to generically detect pipeline stage associations without hardcoding
 * specific association type names.
 *
 * IMPORTANT: Always check `isLoading` before relying on `stageTypeNames` for validation.
 * The set is empty during loading, which can lead to false negatives if not guarded.
 *
 * @param orgId - Organization ID (disabled when falsy)
 * @returns Object with stageTypeNames Set and isLoading boolean
 */
export function useStageTypeNames(
  orgId: number | undefined
): StageTypeNamesResult {
  const { data: objectTypes, isLoading } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId as number),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes — matches useEntityTypeSchema
  });

  const stageTypeNames = useMemo(() => {
    if (!objectTypes) {
      return new Set<string>();
    }
    return new Set(
      objectTypes
        .filter(ot => ot.entity_kind.includes('stage'))
        .map(ot => ot.entity_type_name)
    );
  }, [objectTypes]);

  return { stageTypeNames, isLoading };
}
